import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ThemeProvider } from './hooks/useTheme';
import { UserProvider } from './contexts/UserContext';
import { configureAmplify } from './services/auth';
import { storeIntegrationData, showIntegrationSuccess, showIntegrationError } from './utils/integration';
import { chatbotApi, discordApi, wordpressApi } from './services/api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Chatbots from './pages/Chatbots';
import ChatbotForm from './pages/ChatbotForm';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import AuthCallback from './pages/AuthCallback';
import Admin from './pages/Admin';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [redirecting, setRedirecting] = React.useState(false);
  const [processingIntegration, setProcessingIntegration] = React.useState(false);
  const navigate = useNavigate();

  // Helper functions for Discord integration
  const sanitizeServerName = (serverName: string): string => {
    // Remove special characters, limit length, handle empty names
    let sanitized = serverName
      .replace(/[^a-zA-Z0-9\s-_]/g, '') // Remove special chars except spaces, hyphens, underscores
      .trim()
      .substring(0, 50); // Limit to 50 characters
    
    if (!sanitized) {
      // Generate fallback name if sanitization results in empty string
      const randomNum = Math.floor(Math.random() * 9999) + 1;
      sanitized = `Discord Bot ${randomNum}`;
    }
    
    return sanitized;
  };

  const processDiscordIntegration = async (server: { id: string; name: string }) => {
    try {
      setProcessingIntegration(true);
      
      console.log(`[DEBUG] Checking integration status for Discord server: ${server.id} (${server.name})`);
      toast.loading(`Checking integration status for ${server.name}...`, { id: 'discord-integration' });
      
      // First, check if server is already integrated
      const checkResponse = await discordApi.checkDiscordServerIntegration(server.id);
      
      if (!checkResponse.success) {
        throw new Error('Failed to check server integration status');
      }
      
      const integrationData = checkResponse.data;
      console.log(`[DEBUG] Integration check result:`, integrationData);
      
      if (!integrationData) {
        throw new Error('No integration data received from server');
      }
      
      if (integrationData.exists) {
        if (integrationData.ownedByCurrentUser) {
          // Server already integrated by current user
          toast.dismiss('discord-integration');
          showIntegrationSuccess('discord', 
            `✅ "${server.name}" is already connected to your chatbot "${integrationData.chatbot_name}"!`
          );
          console.log(`[DEBUG] Server already integrated by current user with chatbot: ${integrationData.chatbot_name}`);
        } else {
          // Server integrated by different user
          toast.dismiss('discord-integration');
          showIntegrationError('discord', 
            `❌ "${server.name}" is already connected to another Celsian account. Each Discord server can only be connected to one chatbot at a time.`
          );
          console.log(`[DEBUG] Server already integrated by different user`);
        }
        
        // Navigate to dashboard in both cases
        navigate('/dashboard');
        return;
      }
      
      // Server not integrated - proceed with creating new chatbot and integration
      console.log(`[DEBUG] Server not integrated anywhere, creating new chatbot and integration`);
      
      const sanitizedName = sanitizeServerName(server.name);
      toast.loading(`Creating chatbot "${sanitizedName}" for ${server.name}...`, { id: 'discord-integration' });
      
      const createResponse = await chatbotApi.createChatbot({
        name: sanitizedName,
        description: `Discord bot for ${server.name}`,
      });

      if (!createResponse.success || !createResponse.data) {
        throw new Error('Failed to create chatbot');
      }

      const chatbotId = createResponse.data.chatbotId;
      console.log(`[DEBUG] Created chatbot: ${chatbotId} (${sanitizedName})`);

      // Create Discord integration
      console.log(`[DEBUG] Creating Discord integration for server ${server.id} with chatbot ${chatbotId}`);
      const integrationResponse = await discordApi.createDiscordIntegration(
        server.id,
        chatbotId,
        server.name
      );

      if (!integrationResponse.success) {
        throw new Error('Failed to create Discord integration');
      }

      toast.dismiss('discord-integration');
      showIntegrationSuccess('discord', `Successfully created and linked chatbot "${sanitizedName}" to ${server.name}!`);
      console.log(`[DEBUG] Successfully created integration for server ${server.id}`);
      
      // Navigate to dashboard to show the new chatbot
      navigate('/dashboard');

    } catch (error) {
      console.error('Error processing Discord integration:', error);
      toast.dismiss('discord-integration');
      showIntegrationError('discord', 'Failed to process Discord integration');
    } finally {
      setProcessingIntegration(false);
    }
  };

  const processWordPressIntegration = async (siteUrl: string, siteName: string) => {
    try {
      setProcessingIntegration(true);

      console.log(`[DEBUG] Processing WordPress integration for site: ${siteName} (${siteUrl})`);

      const sanitizedName = siteName.replace(/[^a-zA-Z0-9\s-_]/g, '').trim().substring(0, 50) || 'WordPress Site';
      toast.loading(`Creating chatbot "${sanitizedName}" for ${siteName}...`, { id: 'wordpress-integration' });

      const createResponse = await chatbotApi.createChatbot({
        name: sanitizedName,
        description: `WordPress chatbot for ${siteUrl}`,
      });

      if (!createResponse.success || !createResponse.data) {
        throw new Error('Failed to create chatbot');
      }

      const chatbotId = createResponse.data.chatbotId;
      console.log(`[DEBUG] Created chatbot: ${chatbotId}`);

      toast.dismiss('wordpress-integration');
      showIntegrationSuccess('wordpress',
        `Successfully created chatbot "${sanitizedName}" for ${siteName}!`
      );

      console.log(`[DEBUG] WordPress integration completed successfully`);

      // Redirect back to WordPress if return URL is available
      const returnUrl = sessionStorage.getItem('wordpress_return_url');
      if (returnUrl) {
        sessionStorage.removeItem('wordpress_return_url');
        const url = new URL(returnUrl);
        url.searchParams.set('chatbot_id', chatbotId);
        url.searchParams.set('auto_connected', '1');
        console.log('Redirecting back to WordPress:', url.toString());
        setTimeout(() => { window.location.href = url.toString(); }, 2000);
      } else {
        navigate('/dashboard');
      }

    } catch (error) {
      console.error('Error processing WordPress integration:', error);
      toast.dismiss('wordpress-integration');
      showIntegrationError('wordpress', 'Failed to process WordPress integration');
    } finally {
      setProcessingIntegration(false);
    }
  };

  React.useEffect(() => {
    // Ensure Amplify is configured
    configureAmplify();
    
    // Check if we're coming from an OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.has('code');
    const hasError = urlParams.has('error');
    
    // CRITICAL: Check for integration parameters
    const discordServerId = urlParams.get('discord_server_id');
    const discordServerName = urlParams.get('discord_server_name');
    const source = urlParams.get('source');
    
    // Handle Discord integration
    if (discordServerId && source === 'discord_webhook') {
      if (!isLoading && isAuthenticated && !processingIntegration) {
        // User is already authenticated - process Discord integration directly
        console.log('🔗 Discord integration detected for authenticated user - processing directly...');
        
        const server = {
          id: discordServerId,
          name: discordServerName || 'Discord Server'
        };
        
        // Clean URL first
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('discord_server_id');
        cleanUrl.searchParams.delete('discord_server_name');
        cleanUrl.searchParams.delete('source');
        window.history.replaceState({}, document.title, cleanUrl.toString());
        
        // Process integration directly
        processDiscordIntegration(server);
        
      } else if (!isLoading && !isAuthenticated) {
        // User not authenticated - use cookie flow
        console.log('🔗 Discord integration detected for non-authenticated user - storing cookie...');
        storeIntegrationData('discord', { 
          serverId: discordServerId, 
          serverName: discordServerName || 'Discord Server' 
        });
        
        // Clean URL to remove integration params (but preserve other params like auth callback)
        if (!hasCode && !hasError) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('discord_server_id');
          cleanUrl.searchParams.delete('discord_server_name');
          cleanUrl.searchParams.delete('source');
          window.history.replaceState({}, document.title, cleanUrl.toString());
        }
      }
    } else if (source === 'wordpress_webhook') {
      const siteUrl = urlParams.get('site_url') || '';
      const siteName = urlParams.get('site_name') || '';
      const returnUrl = urlParams.get('return_url') || '';

      if (!isLoading && isAuthenticated && !processingIntegration) {
        // User is already authenticated - process WordPress integration directly
        console.log('WordPress integration detected for authenticated user - processing directly...');

        // Store return_url before cleaning the URL
        if (returnUrl) {
          sessionStorage.setItem('wordpress_return_url', returnUrl);
        }

        // Clean URL first
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('site_url');
        cleanUrl.searchParams.delete('site_name');
        cleanUrl.searchParams.delete('source');
        cleanUrl.searchParams.delete('return_url');
        window.history.replaceState({}, document.title, cleanUrl.toString());

        // Process integration directly
        processWordPressIntegration(siteUrl, siteName);

      } else if (!isLoading && !isAuthenticated) {
        // User not authenticated - use cookie flow
        console.log('WordPress integration detected for non-authenticated user - storing cookie...');
        storeIntegrationData('wordpress', `wp_${Date.now()}`, {
          site_url: siteUrl,
          site_name: siteName,
          return_url: returnUrl
        });

        // Clean URL to remove integration params (but preserve other params like auth callback)
        if (!hasCode && !hasError) {
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('site_url');
          cleanUrl.searchParams.delete('site_name');
          cleanUrl.searchParams.delete('source');
          cleanUrl.searchParams.delete('return_url');
          window.history.replaceState({}, document.title, cleanUrl.toString());
        }
      }
    }
    
    if (!isLoading && !isAuthenticated && !hasCode && !hasError && !redirecting) {
      // Send them to our own login page, not to the Cognito Hosted UI. This
      // used to call Auth.federatedSignIn(), which meant the custom login page
      // was effectively unreachable: "/" and the catch-all both redirect to
      // /dashboard, so any unauthenticated visitor landed on a protected route
      // and was bounced straight out to Cognito's page. /login was only ever
      // seen by someone who typed it.
      //
      // Any integration cookie was already stored above, and it is read after
      // sign-in when the user arrives at /dashboard authenticated, so routing
      // through /login does not disturb the Discord or WordPress flows.
      setRedirecting(true);
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, redirecting, processingIntegration, navigate]);

  if (isLoading || processingIntegration) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {processingIntegration ? 'Processing Discord integration...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      {/* Login.tsx has always linked to both of these; neither had a route, so
          they hit the catch-all and redirected to /login. */}
      <Route
        path="/signup"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Signup />}
      />
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/chatbots"
        element={
          <ProtectedRoute>
            <Chatbots />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chatbots/new"
        element={
          <ProtectedRoute>
            <ChatbotForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chatbots/:chatbotId"
        element={
          <ProtectedRoute>
            <Navigate to="settings" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chatbots/:chatbotId/:section"
        element={
          <ProtectedRoute>
            <ChatbotForm />
          </ProtectedRoute>
        }
      />
      {/* Backwards compatibility redirect */}
      <Route
        path="/chatbots/:chatbotId/edit"
        element={<Navigate to="../settings" relative="path" replace />}
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <UserProvider>
          <Router>
            <div className="App">
              <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4ade80',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            </div>
          </Router>
        </UserProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;